import { useState } from "react";
import { toast } from "sonner";
import { Share2, RotateCcw, Check, Stamp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { EPISODES } from "@/data/content";
import { usePassport } from "@/context/PassportContext";

const StampCircle = ({ e, active, onClick, size = "big" }) => {
  const dim = size === "big" ? "h-24 w-24 sm:h-28 sm:w-28" : "h-20 w-20";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`stamp-${e.code}`}
      aria-pressed={active}
      className={`group relative flex ${dim} shrink-0 flex-col items-center justify-center rounded-full text-center transition-all duration-300 hover:-translate-y-1`}
      style={{
        backgroundColor: active ? e.color + "33" : e.color + "14",
        border: `2px ${active ? "solid" : "dashed"} ${e.color}${active ? "" : "66"}`,
      }}
    >
      <span className="font-mono text-[9px] uppercase tracking-widest text-ink/50">
        EP {e.n}
      </span>
      <span className="font-display text-lg font-extrabold tracking-tight text-ink leading-none">
        {e.code}
      </span>
      <span className="mt-0.5 max-w-[80%] truncate font-mono text-[8px] uppercase tracking-wider text-ink/40">
        {e.city}
      </span>
      {active && (
        <span
          className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full text-white shadow"
          style={{ backgroundColor: e.color }}
        >
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      )}
    </button>
  );
};

export const CollectibleStamps = () => {
  const { isStamped, toggleStamp, count, resetStamps } = usePassport();
  const [open, setOpen] = useState(false);

  const onToggle = (e) => {
    const was = isStamped(e.code);
    toggleStamp(e.code);
    if (!was) toast.success(`¡Sello ${e.code} conseguido!`, { description: e.city });
  };

  const share = async () => {
    const url = `${window.location.origin}/la-ruta`;
    const text = `¡Llevo ${count}/12 sellos en mi pasaporte de Anto&Bella por el Mundo! ✈️ Sella el tuyo:`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Mi pasaporte · Anto&Bella", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("¡Enlace copiado!", { description: "Compártelo con tus amigos." });
      }
    } catch (err) {
      /* usuario canceló el compartir */
    }
  };

  const pct = Math.round((count / 12) * 100);

  return (
    <div data-testid="collectible-stamps">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-coral">
            Sellos coleccionables
          </p>
          <h3 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-ink">
            Doce destinos. Doce historias. Doce versiones de Anto & Bella descubriendo que el mundo cabe en una maleta y en mucha curiosidad.
          </h3>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="open-passport"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-cream transition-all hover:-translate-y-0.5"
            >
              <Stamp className="h-4 w-4" /> Ver mi pasaporte
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-[1.5rem] bg-cream" data-testid="passport-modal">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-ink">
                Tu pasaporte digital
              </DialogTitle>
            </DialogHeader>
            <p className="font-mono text-xs uppercase tracking-widest text-ink/50">
              {count} de 12 sellos · {pct}% completado
            </p>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-ink/10">
              <div
                className="h-full rounded-full bg-coral transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-5 grid max-h-[46vh] grid-cols-3 gap-4 overflow-y-auto sm:grid-cols-4">
              {EPISODES.map((e) => (
                <div key={e.code} className="flex justify-center">
                  <StampCircle e={e} active={isStamped(e.code)} onClick={() => onToggle(e)} size="sm" />
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={share}
                data-testid="share-passport"
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-coral px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5"
              >
                <Share2 className="h-4 w-4" /> Compartir mi pasaporte
              </button>
              <button
                onClick={resetStamps}
                data-testid="reset-passport"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-ink ring-1 ring-ink/15 transition-all hover:ring-ink/40"
              >
                <RotateCcw className="h-4 w-4" /> Reiniciar
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 flex flex-wrap justify-center gap-4 sm:justify-start">
        {EPISODES.map((e) => (
          <StampCircle key={e.code} e={e} active={isStamped(e.code)} onClick={() => onToggle(e)} />
        ))}
      </div>
    </div>
  );
};
